import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageWrapper from "../components/PageWrapper";
import { FadeUp } from "../components/motion";

const MANIFESTO = [
  {
    n: "01",
    title: "Precision over improvisation",
    body: "80–90% of every NUVUN structure is built inside a controlled factory — measured in millimetres, not site-day approximations. Numerically controlled machines, engineered joints, zero weather delays.",
  },
  {
    n: "02",
    title: "Speed without compromise",
    body: "Foundation work and factory production run in parallel. What takes conventional construction eighteen months, our platform delivers in 45–150 days — with tighter tolerances, not looser ones.",
  },
  {
    n: "03",
    title: "Design as the differentiator",
    body: "Modular should never mean monotonous. Every collection is authored — sculptural silhouettes, honest materials, and interiors that photograph like boutique hotels, because they're built like them.",
  },
  {
    n: "04",
    title: "Partnership beyond handover",
    body: "A 20-year structural warranty, a 50+ year design life, and a team that answers the phone. We measure success in decades of performance, not delivery dates.",
  },
];

const STATS = [
  ["600+", "Units delivered"],
  ["3.5 L+", "Sq ft manufactured"],
  ["50+", "Cities served"],
  ["20 yr", "Structural warranty"],
];

export default function About() {
  return (
    <PageWrapper>
      {/* Hero */}
      <section className="relative pt-[76px]" data-testid="about-hero">
        <div className="relative h-[420px] md:h-[520px] overflow-hidden">
          <img src="/images/gen/factory-interior.jpg" alt="NUVUN precision factory" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0908] via-[#0A0908]/60 to-[#0A0908]/30" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14 pb-12 w-full">
              <div className="eyebrow eyebrow-dash text-[#D9A441] mb-4">About NUVUN</div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#F2EDE3] max-w-3xl">
                We build the way the future <span className="italic text-[#D9A441]">deserves to live.</span>
              </h1>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-b border-[#D9A441]/15" data-testid="about-stats">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14 grid grid-cols-2 md:grid-cols-4 divide-x divide-[#D9A441]/12">
          {STATS.map(([v, l]) => (
            <div key={l} className="py-8 text-center">
              <div className="font-condensed text-3xl md:text-4xl text-[#D9A441]">{v}</div>
              <div className="eyebrow !text-[9px] text-[#F2EDE3]/50 mt-2">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Story */}
      <section className="py-20 lg:py-28" data-testid="about-story">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <FadeUp className="lg:col-span-5">
            <div className="eyebrow eyebrow-dash text-[#D9A441] mb-5">The Story</div>
            <h2 className="font-display text-3xl sm:text-4xl text-[#F2EDE3] leading-[1.15]">
              India builds slowly.<br />We decided not to.
            </h2>
          </FadeUp>
          <FadeUp delay={0.12} className="lg:col-span-6 lg:col-start-7 space-y-5 text-sm md:text-base text-[#F2EDE3]/60 leading-relaxed">
            <p>
              NUVUN — an EBG Group company — was founded on a simple conviction: the world's
              best homes, resorts and workspaces shouldn't take years to build, and speed
              shouldn't cost beauty.
            </p>
            <p>
              We curate and engineer a platform of prefab, precast and capsule architecture:
              six collections, thirty-six models, one manufacturing discipline. From a garden
              pod installed in ten days to a forty-key resort opened in five months, every
              NUVUN structure carries the same DNA — precision built, delivered fast, built to last.
            </p>
            <p>
              Today our structures earn revenue for hospitality brands, shelter families on
              their own land, and prove daily that modular is not a compromise — it's an upgrade.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* Manifesto — numbered chapters */}
      <section id="process" className="py-20 lg:py-28 bg-[#0C0B09] border-y border-[#D9A441]/12" data-testid="about-manifesto">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14">
          <FadeUp>
            <div className="eyebrow eyebrow-dash text-[#D9A441] mb-4">The NUVUN Manifesto</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#F2EDE3]">
              Four chapters. <span className="italic text-[#F2EDE3]/70">One standard.</span>
            </h2>
          </FadeUp>
          <div className="mt-14 space-y-0">
            {MANIFESTO.map((m, i) => (
              <FadeUp key={m.n} delay={i * 0.06}>
                <div className="group grid grid-cols-12 gap-4 md:gap-10 py-10 border-b border-[#D9A441]/12 items-start">
                  <div className="col-span-3 md:col-span-2">
                    <span className="font-condensed text-5xl md:text-7xl text-[#D9A441]/25 group-hover:text-[#D9A441]/70 transition-colors duration-500">{m.n}</span>
                  </div>
                  <div className="col-span-9 md:col-span-4">
                    <h3 className="font-display text-2xl md:text-3xl text-[#F2EDE3] leading-tight">{m.title}</h3>
                  </div>
                  <div className="col-span-12 md:col-span-6">
                    <p className="text-sm md:text-base text-[#F2EDE3]/55 leading-relaxed">{m.body}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Pull quote + image */}
      <section className="py-20 lg:py-28" data-testid="about-quote">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <FadeUp className="lg:col-span-6">
            <div className="overflow-hidden rounded-lg gold-frame">
              <img src="/images/gen/about-platform-team.jpg" alt="NUVUN capsule cluster in mountain forest" loading="lazy" className="w-full h-[320px] md:h-[440px] object-cover" />
            </div>
          </FadeUp>
          <FadeUp delay={0.12} className="lg:col-span-5 lg:col-start-8">
            <blockquote className="font-display text-2xl md:text-3xl lg:text-4xl text-[#F2EDE3] leading-[1.25]">
              "We don't sell buildings.<br />
              We sell <span className="italic text-[#D9A441]">certainty</span> — of date,
              of cost, of quality."
            </blockquote>
            <div className="mt-6 text-xs text-[#F2EDE3]/50 uppercase tracking-[0.2em]">— The NUVUN Platform Team</div>
            <Link to="/contact" data-testid="about-cta" className="btn-gold mt-10">
              Start Your Project <ArrowRight className="w-4 h-4" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </PageWrapper>
  );
}
